import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { getFormValues } from 'redux-form';
import { RichTextEditorOptions } from 'components/Shared/Forms/RichTextEditor/RichTextEditorConstants';
import { INFO_WINDOW_POSITION, getDefaultValues } from 'components/InfoWindowSettings/InfoWindowConstants';
import { getActiveMarker } from 'modules/navigation/activeMarker';
import { PANELS, getActivePanel } from 'modules/navigation/activePanel';
import { MARKER_TYPE, getStyledIconOptions } from 'components/MarkerSettings/IconOptions';

// Calculate the pointer offset based on the placement
const getPointerPlacement = (icon, position) => {
    let w = 22;
    let h = 40;
    let aX = 11;
    let aY = 40;
    let top = 0;
    let left = 0;
    let placement = 'top';
    // First determine the size of the marker
    switch (icon.type) {
        case MARKER_TYPE.CUSTOM:
            w = icon.width;
            h = icon.height;
            aX = icon.anchorX;
            aY = icon.anchorY;
            break;

        case MARKER_TYPE.STYLED:
            const opts = getStyledIconOptions(icon);
            if (opts) {
                w = icon.size;
                h = icon.size;
                aX = opts.anchor.x * opts.scale;
                aY = opts.anchor.y * opts.scale;
            }
            break;

        default:
            break;
    }

    // Now calculate the placement
    switch (position) {
        case INFO_WINDOW_POSITION.TOP.value:
            placement = 'top';
            top = -aY;
            left = w / 2 - aX;
            break;

        case INFO_WINDOW_POSITION.BOTTOM.value:
            placement = 'bottom';
            top = h - aY;
            left = w / 2 - aX;
            break;

        case INFO_WINDOW_POSITION.LEFT.value:
            placement = 'left';
            top = h / 2 - aY;
            left = -aX;
            break;

        case INFO_WINDOW_POSITION.RIGHT.value:
            placement = 'right';
            top = h / 2 - aY;
            left = w - aX;
            break;

        default:
            break;
    }
    return { placement, top, left };
};

const snazzyInfoWindowOptions = (state, marker) => {
    const activeMarker = getActiveMarker(state);
    const isActiveMarker = activeMarker && activeMarker.id === marker.id;
    let values = marker.infoWindow;
    if (isActiveMarker) {
        values = getFormValues('info-window')(state) || values;
    }

    if (!values || !values.enabled) {
        return {
            ...marker,
            infoWindow: { enabled: false },
        };
    }

    const { placement, top, left } = getPointerPlacement(marker.icon, values.position);
    const arrow = values.arrow || {};
    const shadow = values.shadow || {};
    const border = values.border || {};
    const container = values.container || {};
    const defaults = getDefaultValues(values.style);

    // The content could be initialized as a string instead of a delta
    let content = values.content;
    if (typeof content !== 'string') {
        content = content.html;
    }
    const activePanel = getActivePanel(state);
    const isEditMode = activePanel === PANELS.INFO_WINDOW || activePanel === PANELS.MARKER;
    const isMapReset = !state.navigation.preview.location;
    const ifSet = (value, defaultValue, format = (x) => x) => {
        return value !== '' ? format(value) : format(defaultValue);
    };
    const pixelFormat = (value) => `${value}px`;

    return {
        ...marker,
        infoWindow: {
            enabled: values.enabled,
            isActive: isActiveMarker,
            isEditMode,
            isMapReset,
            behavior: values.behavior,
            options: {
                wrapperClass: 'ql-snow',
                placement,
                backgroundColor: values.background,
                fontColor: values.fontColor,
                content: ReactDOMServer.renderToStaticMarkup(
                    <div
                        className="ql-editor"
                        style={{
                            fontFamily: RichTextEditorOptions.DefaultFontFamily,
                            fontSize: RichTextEditorOptions.DefaultFontSize,
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: content,
                        }}
                    />
                ),
                maxWidth: ifSet(container.maxWidth),
                maxHeight: ifSet(container.maxHeight),
                padding: ifSet(container.padding, defaults.container.padding, pixelFormat),
                borderRadius: ifSet(container.borderRadius, defaults.container.borderRadius, (x) => `${x}px ${x}px`),
                offset: {
                    top: ifSet(container.top, defaults.container.top, (x) => `${top + x}px`),
                    left: ifSet(container.left, defaults.container.left, (x) => `${left + x}px`),
                },
                border: border.enabled
                    ? {
                          width: ifSet(border.width, defaults.border.width, pixelFormat),
                          color: border.color,
                      }
                    : false,
                pointer: arrow.enabled ? ifSet(arrow.length, defaults.arrow.length, pixelFormat) : false,
                shadow:
                    shadow.enabled && shadow.opacity !== 0
                        ? {
                              h: ifSet(shadow.h, defaults.shadow.h, pixelFormat),
                              v: ifSet(shadow.v, defaults.shadow.v, pixelFormat),
                              blur: ifSet(shadow.blur, defaults.shadow.blur, pixelFormat),
                              spread: ifSet(shadow.spread, defaults.shadow.spread, pixelFormat),
                              opacity: ifSet(shadow.opacity, defaults.shadow.opacity),
                              color: shadow.color,
                          }
                        : false,
                closeOnMapClick: !!values.behavior.closeOnMapClick,
                openOnMarkerClick: !isEditMode || isActiveMarker,
                closeWhenOthersOpen: !!values.behavior.closeWhenOthersOpen,
            },
        },
    };
};

export default snazzyInfoWindowOptions;
